import * as React from 'react';
import { parseISO, format, addDays, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  isSameMonth, isSameDay, isValid, isBefore, differenceInDays, isWeekend } from 'date-fns';

import './styles/calendar';
import { isActiveDate } from './helpers';
import { dateFnsLocale } from '../utils/dateHelper';
import { getLocale } from '../utils/pathHelper';

/* Types  */

export interface IDatePickerProps {
  selectedDateList?: Date[];
  disabledDateList?: Date[];
  handleDateListChange?: Function;
  minDate?: Date;
  maxDate?: Date;
  isRangeSelect: boolean;
  dateDuration?: number;
  isMultiSelect: boolean;
  currentMonth?: Date;
  maxRange?: number;
  information?: { [key: string]: any };
  styles?: { [key: string]: string };
}

interface IDatePickerState {
  currentMonth: Date;
  selectedDateList: Date[];
}

function distinctDate(dateList: Date[] = []): Date[] {
  const timeList = new Set(dateList.map(date => Number(date)).sort((a, b) => a - b));
  return [...Array.from(timeList.values())].map(time => new Date(time));
}

/* React Component  */

class DatePicker extends React.Component<IDatePickerProps, IDatePickerState> {

  static defaultProps = {
    isRangeSelect: true,
    isMultiSelect: false,
    selectedDateList: [],
  }

  constructor(props: IDatePickerProps) {
    super(props);
    this.state = {
      currentMonth: props.currentMonth || new Date(),
      selectedDateList: props.selectedDateList,
    }
    if (props.isRangeSelect && props.isMultiSelect) {
      throw new Error('isRangeSelect and isMultiSelect can not be true at same time.');
    }
  }

  componentDidUpdate(prevProps: IDatePickerProps) {
    if (prevProps.selectedDateList !== this.props.selectedDateList) {
      this.setState({ selectedDateList: this.props.selectedDateList });
    }
  }

  updateDateList(selectedDateList: Date[]): void {
    this.setState({ selectedDateList }, () => {
      const { handleDateListChange } = this.props;
      if (typeof (handleDateListChange) === 'function') {
        handleDateListChange(selectedDateList);
      }
    });
  }

  isActiveDate(selected: Date): boolean {
    const { disabledDateList, minDate, maxDate, maxRange, isRangeSelect } = this.props;
    const { selectedDateList } = this.state;
    return isActiveDate({
      targetDate: selected,
      minDate,
      maxDate,
      maxRange: isRangeSelect && maxRange ? maxRange : null,
      selectedDateList,
      disabledDateList,
      checkTime: false
    });
  }

  handleDateClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const { currentTarget } = event;
    const { isRangeSelect, isMultiSelect, dateDuration } = this.props;
    const { selectedDateList } = this.state;
    const selected: Date = parseISO(currentTarget.getAttribute('data-date'));
    if (!isValid(selected)) { return; }
    if (!this.isActiveDate(selected)) { return; }
    const isAlreadySelected = selectedDateList.find(date => isSameDay(date, selected)) != null;
    const isSelectedBefore = selectedDateList[0] && isBefore(selected, selectedDateList[0]);
    const selectedCount = selectedDateList.length;
    if (dateDuration && selected) {
      let dateList: Date[] = [];
      for (let i = 0; i < dateDuration; i++) {
        dateList.push(addDays(selected, i));
      }
      this.updateDateList(dateList);
    } else if (isRangeSelect) {
      if (isSelectedBefore || selectedCount !== 1 || isAlreadySelected) {
        this.updateDateList([selected])
      } else {
        let dateList: Date[] = [];
        const selectedDuration: number = differenceInDays(selected, selectedDateList[0]) + 1;
        for (let i = 0; i < selectedDuration; i++) {
          dateList.push(addDays(selectedDateList[0], i));
        }
        this.updateDateList(dateList);
      }
    } else if (isMultiSelect) {
      if (isAlreadySelected) {
        this.updateDateList(selectedDateList.filter(date => !isSameDay(date, selected)));
      } else {
        selectedDateList.push(selected);
        this.updateDateList(distinctDate(selectedDateList));
      }
    } else {
      if (isAlreadySelected) {
        this.updateDateList([]);
      } else {
        this.updateDateList([selected]);
      }
    }
  }

  handleNextMonthClick = () => {
    this.setState({
      currentMonth: addMonths(this.state.currentMonth, 1)
    });
  }

  handlePrevMonthClick = () => {
    this.setState({
      currentMonth: addMonths(this.state.currentMonth, -1)
    });
  }

  renderHeader() {
    const currentLocale = getLocale();
    let monthYearFormat = 'MMMM yyyy';

    if (currentLocale === 'ja') {
      monthYearFormat = 'yyyy年M月';
    }

    return (
      <div className="price-calendar__header">
        <div className="icon" onClick={this.handlePrevMonthClick}>
          <i className="fas fa-chevron-left" />
        </div>
        <div className="month">{format(this.state.currentMonth, monthYearFormat, { locale: dateFnsLocale() })}</div>
        <div className="icon" onClick={this.handleNextMonthClick}>
          <i className="fas fa-chevron-right" />
        </div>
      </div>
    );
  }

  renderDays() {
    const dateFormat = 'eee';
    const days = [];
    const startDate = startOfWeek(this.state.currentMonth);
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={`day-${i}`}>
          {format(addDays(startDate, i), dateFormat, { locale: dateFnsLocale() })}
        </div>
      );
    }
    return (
      <div className="price-calendar__days">
        {days}
      </div>
    );
  }

  renderInformation(day: Date) {
    const { information, styles } = this.props;
    if (information == null || Object.keys(information).length === 0) { return null; }
    if (!this.isActiveDate(day)) { return null; }
    const dateString: string = format(day, 'yyyy-MM-dd');
    const info = information[dateString];
    if (info == null) { return null; }

    const elements: JSX.Element[] = [];

    if (typeof info === 'object') {
      Object.keys(info).forEach((key: string) => {
        if (info[key]) {
          elements.push(
            <div className={`price-item ${styles?.calendarInfo}`} key={`${format(day, 'yyyy-MM-dd')}-${key}-price`}>
              <div>{key}</div>
              <div>{info[key]}</div>
            </div>
          );
        };
      });
    } else {
      elements.push(
        <div className={`price-item ${styles?.calendarInfo}`} key={`${format(day, 'yyyy-MM-dd')}-price`}>
          <div>{info}</div>
        </div>
      )
    };

    return (
      <div className="price">
        {elements}
      </div>
    )
  }

  renderCells() {
    const { isMultiSelect, styles } = this.props;
    const { currentMonth, selectedDateList } = this.state;
    const startDateOfMonth: Date    = startOfMonth(currentMonth);
    const endDateOfMonth: Date      = endOfMonth(currentMonth);
    const startDateOfCalendar: Date = startOfWeek(startDateOfMonth);
    const endDateOfCalendar: Date   = endOfWeek(endDateOfMonth);

    const days: JSX.Element[] = [];
    let day: Date = startDateOfCalendar;
    while (day <= endDateOfCalendar) {
      const cssDisabled = this.isActiveDate(day) ? '' : 'disabled';
      const cssOutOfMonth = isSameMonth(day, startDateOfMonth) ? '' : 'out-of-month';
      const isSelected = selectedDateList.some(d => isSameDay(d, day))
      const cssSelected = isSelected ? styles?.calendarCellSelected || 'selected' : '';
      const between = selectedDateList.slice();
      between.shift();
      between.pop();
      const cssTrailing = !isMultiSelect && between.some(d => isSameDay(d, day)) ? 'trailing' : '';
      const cssWeekend = isWeekend(day) ? 'weekend' : '';

      days.push(
        <div
          className={`price-calendar__date ${cssDisabled} ${cssOutOfMonth} ${cssSelected} ${cssTrailing} ${cssWeekend} ${styles && styles.calendarCell}`}
          key={`date-${day}`}
          onClick={this.handleDateClick}
          data-date={format(day, 'yyyy-MM-dd')}
        >
          <div className="date">
            {format(day, 'd')}
          </div>

          {this.renderInformation(day)}
        </div>
      );
      day = addDays(day, 1);
    }
    return (
      <div className="price-calendar__dates">
        {days}
      </div>
    );
  }

  render() {
    return (
      <div className="price-calendar">
        <div>{this.renderHeader()}</div>
        <div>{this.renderDays()}</div>
        <div>{this.renderCells()}</div>
      </div>
    );
  }
}

 export default DatePicker;
